const PACKAGE_TIER_STORAGE_KEY = "synergycoin:packageTier";
const PACKAGE_TIER_EVENT = "synergy:package-tier-updated";

const normalizePackageName = (value) =>
  typeof value === "string" ? value.trim().toLowerCase().replace(/\s+/g, " ") : "";

const resolveTierFromName = (name) => {
  if (!name) return 0;
  if (name.includes("partner")) return 3;
  if (name.includes("member")) return 2;
  if (name.includes("affiliate")) return 2;
  if (name.includes("starter")) return 1;
  if (name.includes("community")) return 2;
  return 0;
};

const resolveTierFromAmount = (amount) => {
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  if (amount >= 500) return 3;
  if (amount >= 200) return 2;
  if (amount >= 25) return 1;
  return 0;
};

const getTransactionBaseAmount = (transaction) => {
  if (!transaction) return 0;
  const original = Number(transaction.metadata?.originalAmount);
  if (Number.isFinite(original) && original > 0) {
    return original;
  }
  const amount = Number(transaction.amount);
  return Number.isFinite(amount) ? amount : 0;
};

const resolveTransactionTier = (transaction) => {
  const nameTier = resolveTierFromName(
    normalizePackageName(transaction?.packageName),
  );
  if (nameTier > 0) {
    return nameTier;
  }
  return resolveTierFromAmount(getTransactionBaseAmount(transaction));
};

const determineHighestTier = (transactions) => {
  if (!Array.isArray(transactions) || !transactions.length) {
    return 0;
  }
  return transactions.reduce((highest, transaction) => {
    const tier = resolveTransactionTier(transaction);
    return tier > highest ? tier : highest;
  }, 0);
};

const broadcastTierUpdate = (payload) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.dispatchEvent(new CustomEvent(PACKAGE_TIER_EVENT, { detail: payload }));
  } catch (error) {
    // ignore event failures
  }
};

const readTierMap = () => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const stored = window.localStorage.getItem(PACKAGE_TIER_STORAGE_KEY);
    if (!stored) {
      return {};
    }
    const parsed = JSON.parse(stored);
    if (
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed)
    ) {
      if (typeof parsed.userId === "string" && "tier" in parsed) {
        return { [parsed.userId]: parsed.tier };
      }
      return parsed;
    }
  } catch (error) {
    // ignore parse errors
  }
  return {};
};

const writeTierMap = (map) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(
      PACKAGE_TIER_STORAGE_KEY,
      JSON.stringify(map),
    );
  } catch (error) {
    // ignore storage failures
  }
};

const readStoredTier = (userId) => {
  if (typeof window === "undefined" || !userId) {
    return null;
  }

  const map = readTierMap();
  const tier = Number(map?.[userId]);
  return Number.isFinite(tier) ? tier : null;
};

const storeTierForUser = (userId, tier) => {
  if (typeof window === "undefined" || !userId) {
    return;
  }
  const normalizedTier = Math.max(0, Number.isFinite(tier) ? tier : 0);
  const map = readTierMap();
  if (map[userId] === normalizedTier) {
    return;
  }
  map[userId] = normalizedTier;
  writeTierMap(map);
  broadcastTierUpdate({ userId, tier: normalizedTier });
};

const clearStoredTier = (userId) => {
  if (typeof window === "undefined") {
    return;
  }

  if (!userId) {
    window.localStorage.removeItem(PACKAGE_TIER_STORAGE_KEY);
    broadcastTierUpdate({ userId: null, tier: 0, cleared: true });
    return;
  }

  const map = readTierMap();
  if (Object.prototype.hasOwnProperty.call(map, userId)) {
    delete map[userId];
    writeTierMap(map);
    broadcastTierUpdate({ userId, tier: 0, cleared: true });
  }
};

const parseAmount = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const numeric = Number(value.replace(/[^0-9.]/g, ""));
    return Number.isFinite(numeric) ? numeric : null;
  }
  return null;
};

const resolveTierFromPackage = (pkg) => {
  if (!pkg) return 0;

  if (typeof pkg === "string") {
    return resolveTierFromName(normalizePackageName(pkg));
  }

  const titleTier = resolveTierFromName(
    normalizePackageName(pkg.title || pkg.name),
  );
  if (titleTier > 0) {
    return titleTier;
  }

  const rawAmount =
    parseAmount(pkg.amount) ??
    parseAmount(pkg.price) ??
    parseAmount(pkg.basePrice) ??
    parseAmount(pkg.cost);

  if (Number.isFinite(rawAmount)) {
    const amountTier = resolveTierFromAmount(rawAmount);
    if (amountTier > 0) {
      return amountTier;
    }
  }

  return 0;
};

const updateStoredTierFromTransactions = (userId, transactions = []) => {
  const tier = determineHighestTier(transactions);
  storeTierForUser(userId, tier);
  return tier;
};

export {
  PACKAGE_TIER_EVENT,
  PACKAGE_TIER_STORAGE_KEY,
  determineHighestTier,
  resolveTierFromPackage,
  storeTierForUser,
  readStoredTier,
  clearStoredTier,
  updateStoredTierFromTransactions,
};
